"use client"

import React, { useState, useEffect } from "react"
import { Box, Button, Typography, Paper, FormHelperText, Chip, CircularProgress } from "@mui/material"
import {
  Business as BusinessIcon,
  Assignment as PlanIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  LocalHospital as SpecialtyIcon,
  Search as SearchIcon,
} from "@mui/icons-material"
import OrganizationAutocomplete from "./OrganizationAutocomplete"
import PlanAutocomplete from "./PlanAutocomplete"
import LocationAutocomplete from "./LocationAutocomplete"
import ProviderAutocomplete from "./ProviderAutocomplete"
import SpecialtyAutocomplete from "./SpecialtyAutocomplete"
import { fetchPatientPlanInformationV2 } from "./providers-api"

const SearchForm = ({ onSearch, loading, isPartnerRoute = false, isPublicRoute = false, planUid = null, partnerOrganizationName = null }) => {
  const [formData, setFormData] = useState({
    organization: null,
    organizationName: "",
    plan: null,
    location: null,
    provider: null,
    specialty: null,
  })

  const [loadingPatientInfo, setLoadingPatientInfo] = useState(true)
  const [initialLocationAddress, setInitialLocationAddress] = useState(null)
  const [initialOrganization, setInitialOrganization] = useState(null)
  const [initialPlan, setInitialPlan] = useState(null)

  const [errors, setErrors] = useState({})

  // Fetch patient plan information on component mount
  useEffect(() => {
    const loadPatientPlanInformation = async () => {
      // Skip API call for partner & public routes - use planUid from URL instead
      if (isPartnerRoute || isPublicRoute) {
        setLoadingPatientInfo(false)
        if (planUid) {
          // Set plan with planUid from URL
          setInitialPlan({ id: planUid, name: 'Partner Plan' })
        }
        // Set organization name from partner if provided
        if (partnerOrganizationName) {
          setFormData(prev => ({
            ...prev,
            organizationName: partnerOrganizationName
          }))
        }
        return
      }
      
      setLoadingPatientInfo(true)
      
      try {
        const patientInfo = await fetchPatientPlanInformationV2()
        
        if (patientInfo && patientInfo.organization && patientInfo.plan) {
          
          const patientOrg = {
            id: patientInfo.organization.id,
            name: patientInfo.organization.name,
          }
          
          const patientPlan = {
            id: patientInfo.plan.id,
            name: patientInfo.plan.name,
          }
          
          // Set initial values for autocomplete components to auto-populate
          setInitialOrganization(patientOrg)
          setInitialPlan(patientPlan)
          
          // Store organization name in formData
          setFormData(prev => ({
            ...prev,
            organization: patientOrg,
            organizationName: patientInfo.organization.name
          }))
          
          // Set initial location address for LocationAutocomplete to auto-populate
          if (patientInfo.location && patientInfo.location.address) {
            setInitialLocationAddress(patientInfo.location.address)
          }
        } else {
          console.error("Patient info is incomplete or missing")
        }
      } catch (error) {
        console.error("Error loading patient plan information:", error)
      } finally {
        // IMPORTANT: Always set loading to false so form displays
        setLoadingPatientInfo(false)
      }
    }

    loadPatientPlanInformation()
  }, [isPartnerRoute, isPublicRoute, planUid, partnerOrganizationName])

  const validateForm = () => {
    const newErrors = {}

    // Organization and Plan validation removed since fields are hidden and auto-populated
    // if (!formData.organization) {
    //   newErrors.organization = "Organization is required"
    // }

    // if (!formData.plan) {
    //   newErrors.plan = "Plan is required"
    // }

    if (!formData.location || !formData.location.placeName) {
      newErrors.location = "Location is required"
    }

    if (!formData.provider && !formData.specialty) {
      newErrors.provider = "Either Provider name or Specialty name is required"
      newErrors.specialty = "Either Provider name or Specialty name is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      onSearch(formData)
    }
  }

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }
      
      // Update organizationName when organization changes
      if (field === "organization") {
        updated.organizationName = value?.name || ""
      }
      
      return updated
    })

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    // Clear provider/specialty cross-validation errors
    if ((field === "provider" || field === "specialty") && value) {
      setErrors((prev) => ({
        ...prev,
        provider: null,
        specialty: null,
      }))
    }

    // Clear location error when location is selected
    if (field === "location" && value && value.placeName) {
      setErrors((prev) => ({ ...prev, location: null }))
    }
  }

  const handleLocationError = (error) => {
    console.error("Location error:", error)
    // You can add additional error handling here if needed
  }

  return (
    <Box sx={{ pl: 0,pr: 3,pb: 2, pt: 2 }}>
      <Typography sx={{ mb: 1, fontSize: 30, fontWeight: 800 }}>
        Find care
      </Typography>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
        {loadingPatientInfo ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading...</Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {/* Organization and Plan Row - Hidden but auto-mapping still works */}
              <Box sx={{ display: "none" }}>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 500, color: "#1976d2", display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <BusinessIcon sx={{ fontSize: 20 }} />
                    Organization name *
                  </Typography>
                  <OrganizationAutocomplete
                    value={formData.organization}
                    onChange={(value) => handleFieldChange("organization", value)}
                    error={!!errors.organization}
                    initialValue={initialOrganization}
                  />
                  {errors.organization && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.organization}
                    </FormHelperText>
                  )}
                </Box>

                <Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 500, color: "#1976d2", display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <PlanIcon sx={{ fontSize: 20 }} />
                    Plan name *
                  </Typography>
                  <PlanAutocomplete
                    value={formData.plan}
                    onChange={(value) => handleFieldChange("plan", value)}
                    organizationId={formData.organization?.id}
                    organizationName={formData.organizationName}
                    disabled={!formData.organization && !initialPlan}
                    error={!!errors.plan}
                    initialValue={initialPlan}
                  />
                  {errors.plan && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.plan}
                    </FormHelperText>
                  )}
                </Box>
              </Box>

              {/* Location, Provider, and Specialty Row - All three fields in one row */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 500, color: "#1976d2", display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <LocationIcon sx={{ fontSize: 20 }} />
                    Location <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <LocationAutocomplete
                    value={formData.location}
                    onChange={(value) => handleFieldChange("location", value)}
                    onError={handleLocationError}
                    error={!!errors.location}
                    placeholder="Enter city, zip code, or use current location"
                    showUseCurrentButton={true}
                    autoDetect={!initialLocationAddress}
                    initialAddress={initialLocationAddress}
                  />
                  {errors.location && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.location}
                    </FormHelperText>
                  )}
                </Box>

                <Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 500, color: "#1976d2", display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <PersonIcon sx={{ fontSize: 20 }} />
                    Provider name
                  </Typography>
                  <ProviderAutocomplete
                    value={formData.provider}
                    onChange={(value) => handleFieldChange("provider", value)}
                    error={!!errors.provider}
                  />
                  {errors.provider && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.provider}
                    </FormHelperText>
                  )}
                </Box>

                <Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 500, color: "#1976d2", display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <SpecialtyIcon sx={{ fontSize: 20 }} />
                    Specialty name
                  </Typography>
                  <SpecialtyAutocomplete
                    value={formData.specialty}
                    onChange={(value) => handleFieldChange("specialty", value)}
                    error={!!errors.specialty}
                    isPublicRoute={isPublicRoute}
                  />
                  {errors.specialty && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.specialty}
                    </FormHelperText>
                  )}
                </Box>
              </Box>

            {/* Search Button */}
            <Box sx={{ display: "flex", justifyContent: "right", mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || loadingPatientInfo}
                startIcon={<SearchIcon />}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </form>
        )}
      </Paper>
    </Box>
  )
}

export default SearchForm
